import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { SpecraOverlayDialog } from '@mono/ui-components/captains';
import { SpecraBadge, SpecraButton, SpecraIconButton, SpecraLink, SpecraSelect, SpecraText } from '@mono/ui-components/ensigns';
import { SpecraEntityCell, SpecraFormField, SpecraInlineBanner, SpecraRestrictedChip } from '@mono/ui-components/lieutenants';
import { Testids } from '@mono/ui-selectors';
import { X } from 'lucide-react';
import { useState } from 'react';
import { RegistryEntityPicker } from '../../../components/SharedRecord/RegistryEntityPicker';
import { renderRouterLink } from '../../../lib/renderRouterLink';
import { RfiEntityConfirm } from './RfiEntityConfirm';
import { ENTITY_ROLE_KINDS, ENTITY_ROLE_LABEL, ENTITY_ROLE_ORDER } from './rfiHomeModel';
import { useConfirmRfiEntity, useLinkRfiEntity, useUnlinkRfiEntity } from './useRfiWorkspaceHome';
const T = Testids.rfiWorkspace;
/** Entities in the rail's role order, then by position. */ export function sortRfiEntities(entities) {
    return [
        ...entities
    ].sort((a, b)=>ENTITY_ROLE_ORDER.indexOf(a.role) - ENTITY_ROLE_ORDER.indexOf(b.role) || a.position - b.position);
}
/** One linked / pending / restricted entity row. */ export function RfiEntityRow({ entity, canEdit, busy, onDecide, onRemove }) {
    const role = ENTITY_ROLE_LABEL[entity.role] ?? entity.role;
    const [confirming, setConfirming] = useState(false);
    if (entity.state === 'pending' || entity.state === 'ambiguous') {
        return /*#__PURE__*/ _jsxs("div", {
            className: "flex flex-col gap-[var(--space-1)]",
            "data-testid": T.entityLinkRow,
            children: [
                /*#__PURE__*/ _jsx(SpecraText, {
                    size: "meta",
                    tone: "muted",
                    children: role
                }),
                /*#__PURE__*/ _jsx(RfiEntityConfirm, {
                    busy: busy,
                    entity: entity,
                    onDecide: onDecide,
                    readOnly: !canEdit
                })
            ]
        });
    }
    if (entity.restricted || entity.state === 'restricted') {
        return /*#__PURE__*/ _jsxs("div", {
            className: "flex items-center gap-[var(--space-2)]",
            "data-testid": T.entityLinkRow,
            children: [
                /*#__PURE__*/ _jsx(SpecraText, {
                    size: "meta",
                    tone: "muted",
                    children: role
                }),
                /*#__PURE__*/ _jsx(SpecraRestrictedChip, {
                    label: "Restricted record"
                })
            ]
        });
    }
    const description = [
        role,
        entity.sublabel
    ].filter(Boolean).join(' · ');
    if (confirming) {
        return /*#__PURE__*/ _jsxs("div", {
            className: "flex items-center gap-[var(--space-2)]",
            "data-testid": T.entityLinkRow,
            children: [
                /*#__PURE__*/ _jsxs(SpecraText, {
                    size: "meta",
                    children: [
                        "Remove ",
                        entity.label,
                        " from this RFI?"
                    ]
                }),
                /*#__PURE__*/ _jsx(SpecraButton, {
                    onClick: ()=>{
                        setConfirming(false);
                        onRemove();
                    },
                    size: "xs",
                    variant: "destructive",
                    children: "Remove"
                }),
                /*#__PURE__*/ _jsx(SpecraButton, {
                    onClick: ()=>setConfirming(false),
                    size: "xs",
                    variant: "ghost",
                    children: "Cancel"
                })
            ]
        });
    }
    return /*#__PURE__*/ _jsx("div", {
        "data-testid": T.entityLinkRow,
        children: /*#__PURE__*/ _jsx(SpecraEntityCell, {
            avatarName: entity.targetKind === 'CONTACT' ? entity.label : undefined,
            description: description,
            title: /*#__PURE__*/ _jsxs("span", {
                className: "inline-flex items-center gap-[var(--space-1-5)]",
                children: [
                    /*#__PURE__*/ _jsx(SpecraLink, {
                        href: entity.href,
                        renderLink: renderRouterLink,
                        children: entity.label
                    }),
                    entity.system ? /*#__PURE__*/ _jsx(SpecraBadge, {
                        variant: "neutral",
                        children: "Shared"
                    }) : null
                ]
            }),
            trailing: canEdit ? /*#__PURE__*/ _jsx(SpecraIconButton, {
                "data-testid": T.entityRemove,
                label: `Remove ${entity.label} from this RFI`,
                onClick: ()=>setConfirming(true),
                size: "sm",
                variant: "ghost",
                children: /*#__PURE__*/ _jsx(X, {})
            }) : undefined
        })
    });
}
function observationOf(draft) {
    return {
        kind: draft.kind,
        name: draft.name ?? '',
        firstName: draft.firstName ?? '',
        lastName: draft.lastName ?? '',
        email: draft.email ?? '',
        phone: draft.phone ?? '',
        title: draft.title ?? '',
        uei: draft.uei ?? '',
        cage: draft.cage ?? '',
        orgCode: draft.orgCode ?? '',
        agency: draft.agency ?? '',
        contractNumber: draft.contractNumber ?? ''
    };
}
/** "Add to this RFI": a role, then GRW-37's picker limited to that role's kinds. */ export function RfiEntityAddDialog({ rfiResponseProjectId, open, onOpenChange }) {
    const [role, setRole] = useState('submission_recipient');
    const link = useLinkRfiEntity(rfiResponseProjectId);
    const done = ()=>onOpenChange(false);
    return /*#__PURE__*/ _jsx(SpecraOverlayDialog, {
        onOpenChange: onOpenChange,
        open: open,
        size: "md",
        testId: T.entityAddDialog,
        title: "Add to this RFI",
        children: /*#__PURE__*/ _jsxs("div", {
            className: "flex flex-col gap-[var(--space-3)]",
            children: [
                /*#__PURE__*/ _jsx(SpecraFormField, {
                    htmlFor: "rfi-entity-role",
                    label: "Role",
                    children: /*#__PURE__*/ _jsx(SpecraSelect, {
                        id: "rfi-entity-role",
                        onChange: (e)=>setRole(e.target.value),
                        value: role,
                        children: Object.keys(ENTITY_ROLE_KINDS).map((r)=>/*#__PURE__*/ _jsx("option", {
                                value: r,
                                children: ENTITY_ROLE_LABEL[r]
                            }, r))
                    })
                }),
                /*#__PURE__*/ _jsx(RegistryEntityPicker, {
                    context: {
                        kind: 'RFI',
                        id: rfiResponseProjectId
                    },
                    createMode: "observation",
                    disabled: link.isPending,
                    kinds: ENTITY_ROLE_KINDS[role] ?? [
                        'CONTACT'
                    ],
                    label: ENTITY_ROLE_LABEL[role] ?? 'Record',
                    onCreateObservation: (draft)=>link.mutate({
                            role,
                            source: 'user',
                            target: {
                                case: 'observation',
                                value: observationOf(draft)
                            }
                        }, {
                            onSuccess: done
                        }),
                    onPick: (option)=>link.mutate({
                            role,
                            source: 'user',
                            target: {
                                case: 'registryRef',
                                value: {
                                    kind: option.kind,
                                    registryId: option.registryId
                                }
                            }
                        }, {
                            onSuccess: done
                        })
                }, role),
                link.error ? /*#__PURE__*/ _jsx(SpecraInlineBanner, {
                    tone: "danger",
                    children: link.error.message
                }) : null
            ]
        })
    });
}
/**
 * People and organizations (WRT-06 rail, v2.3): one row per live entity in
 * role order; pending rows ask REG-54's question inline; walled rows show a
 * restricted chip.
 */ export function RfiEntitiesRailGroup({ rfiResponseProjectId, entities, canEdit }) {
    const confirm = useConfirmRfiEntity(rfiResponseProjectId);
    const unlink = useUnlinkRfiEntity(rfiResponseProjectId);
    const rows = sortRfiEntities(entities);
    return /*#__PURE__*/ _jsxs("div", {
        className: "flex flex-col gap-[var(--space-2)]",
        "data-testid": T.entityLinks,
        children: [
            rows.length === 0 ? /*#__PURE__*/ _jsx(SpecraText, {
                size: "meta",
                tone: "muted",
                children: "No people or organizations linked yet."
            }) : rows.map((e)=>/*#__PURE__*/ _jsx(RfiEntityRow, {
                    busy: confirm.isPending && confirm.variables?.linkId === e.linkId,
                    canEdit: canEdit,
                    entity: e,
                    onDecide: (d)=>confirm.mutate({
                            linkId: e.linkId,
                            decision: d.decision,
                            registryId: d.decision === 'link' ? d.registryId : ''
                        }),
                    onRemove: ()=>unlink.mutate(e.linkId)
                }, e.linkId)),
            confirm.error || unlink.error ? /*#__PURE__*/ _jsx(SpecraInlineBanner, {
                tone: "danger",
                children: (confirm.error ?? unlink.error)?.message
            }) : null
        ]
    });
}
/** The rail group's "Add" action. */ export function RfiEntityAddAction({ rfiResponseProjectId }) {
    const [open, setOpen] = useState(false);
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(SpecraButton, {
                "data-testid": T.entityAdd,
                onClick: ()=>setOpen(true),
                size: "inline",
                variant: "link",
                children: "Add"
            }),
            open ? /*#__PURE__*/ _jsx(RfiEntityAddDialog, {
                onOpenChange: setOpen,
                open: open,
                rfiResponseProjectId: rfiResponseProjectId
            }) : null
        ]
    });
}
